import { useTranslation } from "react-i18next"

import type { EscalationResult, Lane } from "./schemas"

import { Heart, ShieldAlert } from "@/components/icons"

type EscalationBadgeProps = {
  lane: Lane
  escalation: EscalationResult
}

/**
 * Shown in the review step. Tells the user what happens after they hit submit
 * without making them think about routing mechanics.
 *
 * - Escalated (bug that cleared the threshold, or any incident) → orange,
 *   "support will be in touch, expect a reply within 1 business day."
 * - Non-escalated (response feedback or ordinary bug) → muted warm copy,
 *   "we'll take a look" — no talk of "product team" or routing.
 */
export function EscalationBadge({ lane, escalation }: EscalationBadgeProps) {
  const { t } = useTranslation()

  if (escalation.escalate) {
    return (
      <div className="border-primary/30 bg-primary/5 text-foreground rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="text-primary mt-0.5 size-5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-semibold">
              {t("feedback.badge.escalatedTitle", "This goes straight to support")}
            </p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              {t(
                "feedback.badge.escalatedDescription",
                "Someone from the support team will reach out. You should expect a reply within 1 business day."
              )}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const titleKey =
    lane === "response_feedback" ? "feedback.badge.thanksTitle" : "feedback.badge.bugThanksTitle"
  const titleFallback =
    lane === "response_feedback" ? "Thanks — we'll use this" : "We'll take a look"
  const descriptionKey =
    lane === "response_feedback"
      ? "feedback.badge.thanksDescription"
      : "feedback.badge.bugThanksDescription"
  const descriptionFallback =
    lane === "response_feedback"
      ? "Feedback like this directly shapes how we train and tune the assistant."
      : "We'll pick this up as part of our normal development work. Every report helps us improve the product."

  return (
    <div className="bg-muted/40 text-foreground rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <Heart className="text-muted-foreground mt-0.5 size-5 shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-semibold">{t(titleKey, titleFallback)}</p>
          <p className="text-muted-foreground text-xs leading-relaxed">
            {t(descriptionKey, descriptionFallback)}
          </p>
        </div>
      </div>
    </div>
  )
}
